// eslint-disable-next-line no-restricted-imports
import { BaseClientRepresentativeFragment } from 'api/generated/graphql';
import { BaseRepresentativeClientEntity } from 'api/types/entity';
import { lodash } from 'helpers';

import { parseCountryEntity } from '../parseCountryEntity';
import { GenderApi, parseClientGenderType } from '../parseEntity';
import { parseBaseDebtsEntity } from '../parseEntity/parseBaseDebtsEntity';
import { parseIdentityDocumentWithFilesEntity } from '../parseEntity/parseIdentityDocumentEntity';
import { parseKycEntity } from '../parseEntity/parseKycEntity';
import {
  parsePepConnectionEntity,
  PepConnectionTypeApi,
} from '../parseEntity/parsePepConnectionEntity';
import { parseResidentialAddressEntity } from '../parseEntity/parseResidentialAddressEntity';
import { parsePlaceOfBirth } from '../parsePlaceOfBirth';

import { parseBankAccounts } from './parseEntity/parseBankAccounts';
import { parseBiographyEntity } from './parseEntity/parseBiographyEntity';
import { parseFileDocumentEntity } from './parseEntity/parseFileEntity';
import { parseTaxResidenceEntity } from './parseEntity/parseTaxResidenceEntity';

export const parseBaseRepresentativeClientEntity = (
  data: Omit<
    BaseClientRepresentativeFragment,
    '__typename' | 'pepConnectionType' | 'gender'
  > & {
    pepConnectionType?: PepConnectionTypeApi | null;
    gender?: GenderApi | null;
  }
): BaseRepresentativeClientEntity => ({
  id: data.id,
  versionNumber: data.clientVersion?.number,
  versionId: data.clientVersion?.id,
  firstName: data.firstName,
  lastName: data.lastName,
  middleName: data.middleName ?? undefined,
  email: data.email,
  phone: data.phoneNumber ?? undefined,
  dateOfBirth: new Date(data.dateOfBirth),
  gender: data.gender ? parseClientGenderType(data.gender) : undefined,
  clientId: data.clientId ?? '',
  citizenships: data.citizenships
    ? lodash.compact(data.citizenships).map(parseCountryEntity)
    : undefined,
  taxResidences: data.taxResidences
    ? lodash.compact(data.taxResidences).map(parseTaxResidenceEntity)
    : undefined,

  placeOfBirth: data.placeOfBirth
    ? parsePlaceOfBirth(data.placeOfBirth)
    : undefined,
  registrationAddress: data.registrationAddress ?? undefined,
  residentialAddress: parseResidentialAddressEntity(data.residentialAddress),

  biography: data.biography ? parseBiographyEntity(data.biography) : undefined,

  bankAccounts: parseBankAccounts(data.bankAccounts),

  pepConnection: parsePepConnectionEntity(data),

  debts: parseBaseDebtsEntity(data.debts ?? undefined),

  basisForRepresentation: data.basisForRepresentation
    ? {
        documentName: data.basisForRepresentation.documentName,
        dateOfExpiration: new Date(
          data.basisForRepresentation.dateOfExpiration
        ),
        dateOfIssue: new Date(data.basisForRepresentation.dateOfIssue),
        files: lodash
          .compact(data.basisForRepresentation.documents)
          .map(parseFileDocumentEntity),
        documentNumber: data.basisForRepresentation.documentNumber ?? undefined,
        notary: {
          fullName: data.basisForRepresentation.notary?.fullName ?? undefined,
          licenseInfo:
            data.basisForRepresentation.notary?.licenseInfo ?? undefined,
        },
      }
    : undefined,

  additionalInformationFiles: lodash
    .compact(data.additionalInformationDocuments)
    .map(parseFileDocumentEntity),

  kyc: parseKycEntity(data.kyc),

  selfieFiles: data.selfieDocuments
    ? lodash.compact(data.selfieDocuments).map(parseFileDocumentEntity)
    : undefined,

  identityDocument: data.identityDocument
    ? parseIdentityDocumentWithFilesEntity(data.identityDocument)
    : undefined,
});
